import React, { useState, useMemo } from "react";
import { AdminSidebar } from "@/components/admin/AdminSidebar";
import { SidebarProvider, SidebarInset } from "@/components/ui/sidebar";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useTeacherRecentStudents } from "@/hooks/useTeacherRecentStudents";
import { TeacherStudentsTable } from "@/components/admin/teacher-students/TeacherStudentsTable";
import { Users, BookOpen, Clock, Download, Loader2, Calculator } from "lucide-react";
import * as XLSX from "xlsx";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const MONTHS = [
  { value: 1, label: "มกราคม" },
  { value: 2, label: "กุมภาพันธ์" },
  { value: 3, label: "มีนาคม" },
  { value: 4, label: "เมษายน" },
  { value: 5, label: "พฤษภาคม" },
  { value: 6, label: "มิถุนายน" },
  { value: 7, label: "กรกฎาคม" },
  { value: 8, label: "สิงหาคม" },
  { value: 9, label: "กันยายน" },
  { value: 10, label: "ตุลาคม" },
  { value: 11, label: "พฤศจิกายน" },
  { value: 12, label: "ธันวาคม" },
];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

const AdminTeacherStudentsPage: React.FC = () => {
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isCalculating, setIsCalculating] = useState(false);

  const { data, isLoading, error } = useTeacherRecentStudents(selectedYear, selectedMonth);

  const handleCalculateDepletion = async () => {
    if (!data || data.length === 0) return;
    setIsCalculating(true);
    try {
      const uniqueStudentCodes = [...new Set(data.map(r => r.student_id))];
      const { data: result, error } = await supabase.functions.invoke('calculate-hours-depletion', {
        body: { student_codes: uniqueStudentCodes }
      });
      if (error) throw error;
      toast.success(`คำนวณวันหมดคอร์สสำเร็จ (${result?.students_processed ?? 0} คน)`);
    } catch (err: any) {
      console.error('Error calculating depletion:', err);
      toast.error('เกิดข้อผิดพลาดในการคำนวณวันหมดคอร์ส');
    } finally {
      setIsCalculating(false);
    }
  };

  const summary = useMemo(() => {
    if (!data || data.length === 0) {
      return { uniqueStudents: 0, totalClasses: 0, totalHours: 0 };
    }

    const uniqueStudents = new Set(data.map((row) => row.student_id)).size;
    const totalClasses = data.reduce((acc, row) => acc + (row.total_class || 0), 0);
    const totalHours = data.reduce((acc, row) => acc + (row.total_hours || 0), 0);

    return { uniqueStudents, totalClasses, totalHours };
  }, [data]);

  const handleExportExcel = () => {
    if (!data || data.length === 0) return;

    const exportData = data.map((item) => ({
      'รหัสครู': item.teacher_id,
      'ชื่อเล่นครู': item.nickname || '',
      'ชื่อครู': item.full_name || '',
      'รหัสนักเรียน': item.student_id,
      'ชื่อนักเรียน': [item.student_first_name, item.student_last_name].filter(Boolean).join(' '),
      'ชื่อเล่นนักเรียน': item.student_nickname || '',
      'จำนวนคลาส': item.total_class,
      'รวมชั่วโมง': item.total_hours,
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Teacher Students');
    XLSX.writeFile(workbook, `teacher_students_${selectedYear}_${String(selectedMonth).padStart(2, '0')}.xlsx`);
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full">
        <AdminSidebar />
        <SidebarInset className="flex-1 p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div>
              <h1 className="text-2xl font-bold">รายงานนักเรียนแต่ละครู</h1>
              <p className="text-muted-foreground">
                แสดงข้อมูลว่านักเรียนแต่ละคนเรียนกับครูคนไหน เรียนกี่ชั่วโมง จากกี่คลาส
              </p>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="pt-6">
                <div className="flex flex-wrap gap-4 items-end">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">ปี</label>
                    <Select
                      value={String(selectedYear)}
                      onValueChange={(val) => setSelectedYear(Number(val))}
                    >
                      <SelectTrigger className="w-[120px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {YEARS.map((year) => (
                          <SelectItem key={year} value={String(year)}>
                            {year}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">เดือน</label>
                    <Select
                      value={String(selectedMonth)}
                      onValueChange={(val) => setSelectedMonth(Number(val))}
                    >
                      <SelectTrigger className="w-[150px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.map((month) => (
                          <SelectItem key={month.value} value={String(month.value)}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <Button
                    onClick={handleExportExcel}
                    disabled={!data || data.length === 0}
                    variant="outline"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Excel
                  </Button>

                  <Button
                    onClick={handleCalculateDepletion}
                    disabled={!data || data.length === 0 || isCalculating}
                    variant="outline"
                  >
                    {isCalculating ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Calculator className="h-4 w-4 mr-2" />
                    )}
                    คำนวณวันหมดคอร์ส
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">จำนวนนักเรียน</CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summary.uniqueStudents}</div>
                  <p className="text-xs text-muted-foreground">คน</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">รวมคลาสทั้งหมด</CardTitle>
                  <BookOpen className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summary.totalClasses}</div>
                  <p className="text-xs text-muted-foreground">คลาส</p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between pb-2">
                  <CardTitle className="text-sm font-medium">รวมชั่วโมงทั้งหมด</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{summary.totalHours}</div>
                  <p className="text-xs text-muted-foreground">ชั่วโมง</p>
                </CardContent>
              </Card>
            </div>

            {/* Data Table */}
            <Card>
              <CardHeader>
                <CardTitle>รายละเอียดการสอน</CardTitle>
                <CardDescription>
                  {MONTHS.find((m) => m.value === selectedMonth)?.label} {selectedYear}
                </CardDescription>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex justify-center items-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                ) : error ? (
                  <div className="text-center py-8 text-destructive">
                    เกิดข้อผิดพลาดในการโหลดข้อมูล
                  </div>
                ) : (
                  <TeacherStudentsTable data={data || []} />
                )}
              </CardContent>
            </Card>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default AdminTeacherStudentsPage;
